/**
 * Tier & Feature Utilities
 *
 * Functions for checking billing tier requirements and feature access.
 * Supports the tiered feature model: trial -> starter -> professional -> enterprise
 */

import type { BillingTier, TierFeatures, TierLimits, FeatureAccess } from './types';
import logger from '@/lib/logger';

// ============================================================================
// Tier Order (lowest to highest)
// ============================================================================

const TIER_ORDER: readonly BillingTier[] = ['trial', 'starter', 'professional', 'enterprise'];

/**
 * Numeric value for each tier (for comparisons)
 */
const TIER_LEVEL: Record<BillingTier, number> = {
  trial: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

// ============================================================================
// Tier Features Configuration
// ============================================================================

/**
 * Features available at each tier level
 */
const TIER_FEATURES: Record<BillingTier, TierFeatures> = {
  trial: {
    // Core features
    basic_calls: true,
    ai_assist: true, // Limited during trial
    transcription: true, // Limited during trial

    // Advanced features
    advanced_analytics: false,
    custom_branding: false,
    api_access: false,
    sso_integration: false,

    // Enterprise features
    dedicated_support: false,
    custom_integrations: false,
    audit_logs: false,
    data_export: false,

    // Limits
    max_users: 5,
    max_concurrent_calls: 2,
    max_storage_gb: 5,
    retention_days: 30,
  },

  starter: {
    // Core features
    basic_calls: true,
    ai_assist: true,
    transcription: true,

    // Advanced features
    advanced_analytics: false,
    custom_branding: false,
    api_access: true,
    sso_integration: false,

    // Enterprise features
    dedicated_support: false,
    custom_integrations: false,
    audit_logs: false,
    data_export: true,

    // Limits
    max_users: 25,
    max_concurrent_calls: 10,
    max_storage_gb: 50,
    retention_days: 90,
  },

  professional: {
    // Core features
    basic_calls: true,
    ai_assist: true,
    transcription: true,

    // Advanced features
    advanced_analytics: true,
    custom_branding: true,
    api_access: true,
    sso_integration: true,

    // Enterprise features
    dedicated_support: false,
    custom_integrations: false,
    audit_logs: true,
    data_export: true,

    // Limits
    max_users: 100,
    max_concurrent_calls: 50,
    max_storage_gb: 500,
    retention_days: 365,
  },

  enterprise: {
    // Core features
    basic_calls: true,
    ai_assist: true,
    transcription: true,

    // Advanced features
    advanced_analytics: true,
    custom_branding: true,
    api_access: true,
    sso_integration: true,

    // Enterprise features
    dedicated_support: true,
    custom_integrations: true,
    audit_logs: true,
    data_export: true,

    // Limits
    max_users: -1, // Unlimited
    max_concurrent_calls: -1, // Unlimited
    max_storage_gb: -1, // Unlimited
    retention_days: -1, // Unlimited (custom)
  },
};

// ============================================================================
// Tier Limits Configuration
// ============================================================================

/**
 * Detailed limits for each tier
 */
const TIER_LIMITS: Record<BillingTier, TierLimits> = {
  trial: {
    max_users: 5,
    max_concurrent_calls: 2,
    max_storage_gb: 5,
    max_departments: 1,
    retention_days: 30,
    api_rate_limit: 100, // requests per minute
  },

  starter: {
    max_users: 25,
    max_concurrent_calls: 10,
    max_storage_gb: 50,
    max_departments: 3,
    retention_days: 90,
    api_rate_limit: 500,
  },

  professional: {
    max_users: 100,
    max_concurrent_calls: 50,
    max_storage_gb: 500,
    max_departments: 10,
    retention_days: 365,
    api_rate_limit: 2000,
  },

  enterprise: {
    max_users: -1, // Unlimited
    max_concurrent_calls: -1, // Unlimited
    max_storage_gb: -1, // Unlimited
    max_departments: -1, // Unlimited
    retention_days: -1, // Custom
    api_rate_limit: -1, // Custom
  },
};

// ============================================================================
// Tier Comparison Functions
// ============================================================================

/**
 * Check if actual tier meets or exceeds required tier
 *
 * @param actualTier - The user's current tier
 * @param requiredTier - The minimum tier required
 * @returns true if actual tier is equal to or higher than required
 *
 * @example
 * ```ts
 * meetsTierRequirement('professional', 'starter'); // true
 * meetsTierRequirement('starter', 'professional'); // false
 * meetsTierRequirement('enterprise', 'enterprise'); // true
 * ```
 */
export function meetsTierRequirement(
  actualTier: BillingTier,
  requiredTier: BillingTier
): boolean {
  return TIER_LEVEL[actualTier] >= TIER_LEVEL[requiredTier];
}

/**
 * Compare two tiers
 *
 * @param tierA - First tier
 * @param tierB - Second tier
 * @returns -1 if tierA < tierB, 0 if equal, 1 if tierA > tierB
 */
export function compareTiers(tierA: BillingTier, tierB: BillingTier): -1 | 0 | 1 {
  const levelA = TIER_LEVEL[tierA];
  const levelB = TIER_LEVEL[tierB];

  if (levelA < levelB) return -1;
  if (levelA > levelB) return 1;
  return 0;
}

/**
 * Get the higher of two tiers
 *
 * @param tierA - First tier
 * @param tierB - Second tier
 * @returns The higher tier
 */
export function getHigherTier(tierA: BillingTier, tierB: BillingTier): BillingTier {
  return TIER_LEVEL[tierA] >= TIER_LEVEL[tierB] ? tierA : tierB;
}

/**
 * Get the next tier upgrade option
 *
 * @param currentTier - Current tier
 * @returns Next tier or null if already at highest
 */
export function getNextTier(currentTier: BillingTier): BillingTier | null {
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex >= TIER_ORDER.length - 1) {
    return null;
  }
  return TIER_ORDER[currentIndex + 1];
}

// ============================================================================
// Feature Check Functions
// ============================================================================

/**
 * Check if a specific feature is available at a tier
 *
 * @param tier - The billing tier to check
 * @param feature - The feature key to check
 * @returns true if feature is available
 *
 * @example
 * ```ts
 * tierHasFeature('professional', 'advanced_analytics'); // true
 * tierHasFeature('starter', 'sso_integration'); // false
 * ```
 */
export function tierHasFeature(
  tier: BillingTier,
  feature: keyof TierFeatures
): boolean {
  const features = TIER_FEATURES[tier];
  const value = features[feature];

  // For boolean features, return the value directly
  if (typeof value === 'boolean') {
    return value;
  }

  // For numeric limits, check if > 0 (or -1 for unlimited)
  if (typeof value === 'number') {
    return value !== 0;
  }

  return false;
}

/**
 * Check feature access with detailed result
 *
 * @param tier - The user's billing tier
 * @param feature - The feature to check
 * @returns Feature access result with upgrade info
 *
 * @example
 * ```ts
 * const access = checkFeatureAccess('starter', 'sso_integration');
 * if (!access.allowed) {
 *   logger.debug('Tiers', 'Upgrade to for SSO', { access: access.required_tier });
 * }
 * ```
 */
export function checkFeatureAccess(
  tier: BillingTier,
  feature: keyof TierFeatures
): FeatureAccess {
  if (tierHasFeature(tier, feature)) {
    return { allowed: true };
  }

  // Find minimum tier that has this feature
  const requiredTier = TIER_ORDER.find((t) => tierHasFeature(t, feature));

  return {
    allowed: false,
    reason: `Feature '${feature}' requires ${requiredTier || 'enterprise'} tier`,
    required_tier: requiredTier,
    upgrade_url: requiredTier ? `/settings/billing?upgrade=${requiredTier}` : undefined,
  };
}

/**
 * Get all features for a tier
 *
 * @param tier - The billing tier
 * @returns All feature flags for the tier
 */
export function getTierFeatures(tier: BillingTier): TierFeatures {
  return { ...TIER_FEATURES[tier] };
}

// ============================================================================
// Limit Functions
// ============================================================================

/**
 * Get all limits for a tier
 *
 * @param tier - The billing tier
 * @returns All limits for the tier
 *
 * @example
 * ```ts
 * const limits = getTierLimits('professional');
 * logger.debug('Tiers', 'Max users', { limits: limits.max_users }); // 100
 * ```
 */
export function getTierLimits(tier: BillingTier): TierLimits {
  return { ...TIER_LIMITS[tier] };
}

/**
 * Get a specific limit for a tier
 *
 * @param tier - The billing tier
 * @param limitKey - The specific limit to get
 * @returns The limit value (-1 for unlimited)
 */
export function getTierLimit(tier: BillingTier, limitKey: keyof TierLimits): number {
  return TIER_LIMITS[tier][limitKey];
}

/**
 * Check if a limit is unlimited for a tier
 *
 * @param tier - The billing tier
 * @param limitKey - The limit to check
 * @returns true if unlimited (-1)
 */
export function isLimitUnlimited(tier: BillingTier, limitKey: keyof TierLimits): boolean {
  return TIER_LIMITS[tier][limitKey] === -1;
}

/**
 * Check if current usage exceeds tier limit
 *
 * @param tier - The billing tier
 * @param limitKey - The limit to check
 * @param currentUsage - Current usage count
 * @returns true if limit is exceeded (false if unlimited)
 */
export function isLimitExceeded(
  tier: BillingTier,
  limitKey: keyof TierLimits,
  currentUsage: number
): boolean {
  const limit = TIER_LIMITS[tier][limitKey];

  // -1 means unlimited
  if (limit === -1) {
    return false;
  }

  return currentUsage >= limit;
}

/**
 * Get remaining capacity for a limit
 *
 * @param tier - The billing tier
 * @param limitKey - The limit to check
 * @param currentUsage - Current usage count
 * @returns Remaining capacity (-1 if unlimited, 0 if exceeded)
 */
export function getRemainingCapacity(
  tier: BillingTier,
  limitKey: keyof TierLimits,
  currentUsage: number
): number {
  const limit = TIER_LIMITS[tier][limitKey];

  // -1 means unlimited
  if (limit === -1) {
    return -1;
  }

  return Math.max(0, limit - currentUsage);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get display name for a tier
 *
 * @param tier - The billing tier
 * @returns Human-readable tier name
 */
export function getTierDisplayName(tier: BillingTier): string {
  const displayNames: Record<BillingTier, string> = {
    trial: 'Trial',
    starter: 'Starter',
    professional: 'Professional',
    enterprise: 'Enterprise',
  };

  return displayNames[tier];
}

/**
 * Get all available tiers
 *
 * @returns Array of all tiers in order
 */
export function getAllTiers(): readonly BillingTier[] {
  return TIER_ORDER;
}

/**
 * Validate that a string is a valid tier
 *
 * @param value - Value to validate
 * @returns true if valid tier
 */
export function isValidTier(value: unknown): value is BillingTier {
  return typeof value === 'string' && TIER_ORDER.includes(value as BillingTier);
}

/**
 * Get minimum tier required for a feature
 *
 * @param feature - The feature to check
 * @returns Minimum tier required, or null if not available at any tier
 */
export function getMinimumTierForFeature(
  feature: keyof TierFeatures
): BillingTier | null {
  for (const tier of TIER_ORDER) {
    if (tierHasFeature(tier, feature)) {
      return tier;
    }
  }
  return null;
}
